import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { CarCard } from "../components/CarCard";
import { api } from "../lib/api";
import { useWishlist } from "../lib/wishlist";
import { Heart, ArrowRight } from "lucide-react";

export default function Wishlist() {
  const { ids, remove } = useWishlist();
  const [allCars, setAllCars] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/cars", { params: { include_sold: true } });
        if (alive) setAllCars(r.data || []);
      } catch {
        if (alive) setAllCars([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  const saved = useMemo(() => {
    if (!allCars) return null;
    const byId = new Map(allCars.map((c) => [c.id, c]));
    const found = ids.map((id) => byId.get(id)).filter(Boolean);
    // Cleanup deleted cars from localStorage
    ids.forEach((id) => {
      if (!byId.has(id)) remove(id);
    });
    return found;
  }, [allCars, ids, remove]);

  return (
    <div className="App min-h-screen flex flex-col" data-testid="wishlist-page">
      <Header />
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24 w-full">
        <div className="inline-flex items-center gap-3 mb-6">
          <span className="divider-gold" />
          <span className="text-gold text-xs uppercase tracking-[0.3em] font-medium">I tuoi preferiti</span>
        </div>
        <h1 className="font-display text-5xl md:text-6xl font-bold text-midnight mb-4 leading-tight">
          Auto <span className="italic text-gold">salvate</span>
        </h1>
        <p className="text-midnight/70 text-lg max-w-2xl leading-relaxed mb-12">
          Le auto che hai messo nei preferiti restano qui anche quando torni. I dati sono salvati solo su questo dispositivo, nessun account necessario.
        </p>

        {saved === null ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="card-sharp overflow-hidden">
                <div className="aspect-video bg-muted animate-pulse" />
                <div className="p-7 space-y-3">
                  <div className="h-4 w-1/3 bg-muted animate-pulse" />
                  <div className="h-6 w-2/3 bg-muted animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : saved.length === 0 ? (
          <div className="card-sharp p-16 text-center max-w-2xl" data-testid="wishlist-empty">
            <Heart size={40} className="text-gold/40 mx-auto mb-4" />
            <h3 className="font-display text-2xl mb-3">Nessuna auto salvata</h3>
            <p className="text-midnight/60 mb-6">
              Sfoglia il parco auto e tocca il cuoricino sulle auto che ti piacciono. Le troverai qui al tuo prossimo passaggio.
            </p>
            <Link
              to="/"
              className="btn-gold inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-widest font-semibold"
              data-testid="wishlist-browse-btn"
            >
              Sfoglia il Parco Auto <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="wishlist-grid">
            {saved.map((c, i) => (
              <CarCard key={c.id} car={c} index={i} />
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
